import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, ShieldCheck, BarChart3, Megaphone } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { langPath } from "@/lib/routes";
import {
  DENIED_PREFS,
  GRANTED_PREFS,
  readStoredConsent,
  reapplyStoredConsent,
  saveConsent,
} from "@/lib/consent";

const Toggle = ({ checked, disabled, onChange, testId, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    data-testid={testId}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber ${
      checked ? "border-ink bg-ink" : "border-hairline bg-white"
    } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
  >
    <span
      className={`ml-0.5 h-5 w-5 rounded-full transition-transform duration-200 ${
        checked ? "translate-x-[19px] bg-amber2" : "translate-x-0 bg-hairline"
      }`}
    />
  </button>
);

export const CookieConsent = ({ openSignal }) => {
  const { t, lang } = useI18n();
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [prefs, setPrefs] = useState(DENIED_PREFS);

  useEffect(() => {
    const stored = reapplyStoredConsent();
    if (stored) {
      setPrefs({ necessary: true, analytics: stored.analytics, ads: stored.ads });
      setVisible(false);
    } else {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (!openSignal) return;
    const stored = readStoredConsent();
    setPrefs(
      stored
        ? { necessary: true, analytics: stored.analytics, ads: stored.ads }
        : DENIED_PREFS,
    );
    setModalOpen(true);
  }, [openSignal]);

  const commit = useCallback((next) => {
    saveConsent(next);
    setPrefs(next);
    setModalOpen(false);
    setVisible(false);
  }, []);

  const acceptAll = () => commit(GRANTED_PREFS);
  const rejectAll = () => commit({ ...DENIED_PREFS });
  const savePrefs = () => commit({ ...prefs, necessary: true });

  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setModalOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [modalOpen]);

  const categories = [
    {
      key: "necessary",
      icon: ShieldCheck,
      title: t("cookie.modal.categories.necessary.title"),
      body: t("cookie.modal.categories.necessary.body"),
      locked: true,
      testId: "cookie-necessary-switch",
    },
    {
      key: "analytics",
      icon: BarChart3,
      title: t("cookie.modal.categories.analytics.title"),
      body: t("cookie.modal.categories.analytics.body"),
      locked: false,
      testId: "cookie-analytics-switch",
    },
    {
      key: "ads",
      icon: Megaphone,
      title: t("cookie.modal.categories.ads.title"),
      body: t("cookie.modal.categories.ads.body"),
      locked: false,
      testId: "cookie-ads-switch",
    },
  ];

  return (
    <>
      {visible && !modalOpen && (
        <div
          data-testid="cookie-banner"
          role="region"
          aria-label={t("cookie.banner.title")}
          className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 sm:px-5 sm:pb-5"
        >
          <div className="mx-auto w-full max-w-5xl rounded-2xl border border-hairline bg-white p-5 shadow-card sm:p-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center">
              <div className="flex flex-1 gap-3.5">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-paper2">
                  <Cookie className="h-4.5 w-4.5 text-amber" strokeWidth={1.75} />
                </span>
                <div>
                  <p className="font-display text-[1.05rem] font-semibold text-ink">
                    {t("cookie.banner.title")}
                  </p>
                  <p className="mt-1.5 max-w-[70ch] text-[0.82rem] leading-6 text-muted-ink">
                    {t("cookie.banner.body")}
                  </p>
                  <Link
                    to={langPath(lang, "cookies")}
                    data-testid="cookie-banner-policy-link"
                    className="link-underline mt-2 inline-block text-[0.78rem] font-medium text-ink"
                  >
                    {t("cookie.banner.policyLink")}
                  </Link>
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row lg:shrink-0">
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  data-testid="cookie-manage"
                  className="rounded-xl border border-hairline px-4 py-2.5 text-[0.8rem] font-medium text-ink transition-colors duration-200 hover:bg-paper2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber"
                >
                  {t("cookie.banner.manage")}
                </button>
                <button
                  type="button"
                  onClick={rejectAll}
                  data-testid="cookie-reject-all"
                  className="rounded-xl border border-hairline px-4 py-2.5 text-[0.8rem] font-semibold text-ink transition-colors duration-200 hover:bg-paper2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber"
                >
                  {t("cookie.banner.rejectAll")}
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  data-testid="cookie-accept-all"
                  className="rounded-xl bg-ink px-4 py-2.5 text-[0.8rem] font-semibold text-white transition-colors duration-200 hover:bg-ink2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2"
                >
                  {t("cookie.banner.acceptAll")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center p-3 sm:items-center sm:p-6">
          <div
            className="absolute inset-0 bg-ink/40"
            onClick={() => setModalOpen(false)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t("cookie.modal.title")}
            data-testid="cookie-preferences-modal"
            className="relative z-10 max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-hairline bg-white p-5 shadow-card sm:p-7"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-[1.4rem] font-semibold leading-tight text-ink">
                  {t("cookie.modal.title")}
                </h2>
                <p className="mt-2 max-w-[62ch] text-[0.84rem] leading-6 text-muted-ink">
                  {t("cookie.modal.body")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                aria-label={t("common.close")}
                data-testid="cookie-modal-close"
                className="shrink-0 rounded-lg border border-hairline p-2 text-ink transition-colors duration-200 hover:bg-paper2"
              >
                <X className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </div>

            <div className="mt-6 space-y-3">
              {categories.map((c) => {
                const Icon = c.icon;
                const checked = c.locked ? true : Boolean(prefs[c.key]);
                return (
                  <div
                    key={c.key}
                    className="rounded-xl border border-hairline bg-paper p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex gap-3">
                        <Icon
                          className="mt-0.5 h-4.5 w-4.5 shrink-0 text-amber"
                          strokeWidth={1.75}
                        />
                        <div>
                          <p className="text-[0.9rem] font-semibold text-ink">
                            {c.title}
                          </p>
                          <p className="mt-1 max-w-[60ch] text-[0.78rem] leading-5 text-muted-ink">
                            {c.body}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <Toggle
                          checked={checked}
                          disabled={c.locked}
                          label={c.title}
                          testId={c.testId}
                          onChange={(v) =>
                            setPrefs((p) => ({ ...p, [c.key]: v, necessary: true }))
                          }
                        />
                        <span className="font-mono text-[0.58rem] uppercase tracking-[0.12em] text-muted-ink/70">
                          {c.locked
                            ? t("cookie.modal.categories.necessary.always")
                            : checked
                              ? t("cookie.modal.statusOn")
                              : t("cookie.modal.statusOff")}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-5 text-[0.74rem] leading-5 text-muted-ink/85">
              {t("cookie.modal.footNote")}
            </p>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-modal-reject-all"
                className="rounded-xl border border-hairline px-4 py-2.5 text-[0.8rem] font-medium text-ink transition-colors duration-200 hover:bg-paper2"
              >
                {t("cookie.modal.rejectAll")}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-modal-accept-all"
                className="rounded-xl border border-hairline px-4 py-2.5 text-[0.8rem] font-medium text-ink transition-colors duration-200 hover:bg-paper2"
              >
                {t("cookie.modal.acceptAll")}
              </button>
              <button
                type="button"
                onClick={savePrefs}
                data-testid="cookie-save-preferences"
                className="rounded-xl bg-ink px-5 py-2.5 text-[0.8rem] font-semibold text-white transition-colors duration-200 hover:bg-ink2"
              >
                {t("cookie.modal.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
